import axios from 'axios';

// Use relative path for API calls to leverage the dev server proxy or same-origin in production.
// This allows access from any device (localhost, smartphone IP, etc) without hardcoding IPs.
const API_URL = '/api';
const AUTH_TOKEN_KEY = 'auth_token';

console.log('🔗 Connecting to Backend at:', API_URL);

const apiClient = axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `mc_token=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secureFlag}`;
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    document.cookie = 'mc_token=; Path=/; Max-Age=0; SameSite=Lax';
  }
};

export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

export const loginUser = async (username, password) => {
  try {
    const response = await apiClient.post('/auth/login', { username, password });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const loginWith2FA = async (userId, token) => {
  try {
    const response = await apiClient.post('/auth/2fa/login', { userId, token });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const registerUser = async (username, email, password) => {
  try {
    const response = await apiClient.post('/auth/register', { username, email, password });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await apiClient.get('/auth/me');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const logoutUser = async () => {
  try {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get all items with optional filters
export const getItems = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      const val = filters[key];
      // Skip empty values and client-side sentinel 'any'
      if (val === undefined || val === null) return;
      if (typeof val === 'string' && val.toLowerCase() === 'any') return;
      if (val === '') return;
      params.append(key, val);
    });
    
    const response = await apiClient.get(`/items?${params.toString()}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get single item
export const getItem = async (id) => {
  try {
    const response = await apiClient.get(`/items/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Analyze coin images
export const analyzeCoin = async (formData) => {
  try {
    const response = await apiClient.post('/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Create new item
export const createItem = async (formData) => {
  try {
    const response = await apiClient.post('/items', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Update item
export const updateItem = async (id, formData) => {
  try {
    const response = await apiClient.put(`/items/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Delete item
export const deleteItem = async (id) => {
  try {
    const response = await apiClient.delete(`/items/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get statistics
export const getStatistics = async () => {
  try {
    const response = await apiClient.get('/statistics');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get coin value from API
export const getCoinValue = async (country, year, denomination) => {
  try {
    const response = await apiClient.get('/coin-value', {
      params: { country, year, denomination }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Get country stats for map
export const getCountryStats = async () => {
  try {
    const response = await apiClient.get('/items-stats/countries');
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const searchNumista = async (query) => {
    try {
    const response = await apiClient.get('/numista/search', {
            params: { query }
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getNumistaDetails = async (id) => {
    try {
    const response = await apiClient.get(`/numista/coin/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getNumistaCoinIssues = async (id) => {
    try {
    const response = await apiClient.get(`/numista/coin/${id}/issues`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const addToNumistaCollection = async (data) => {
    try {
    const response = await apiClient.post('/numista/collection', data);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const exchangeNumistaToken = async (code, state) => {
  try {
    const response = await apiClient.post('/numista/token', { code, state });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const rotateImage = async (id, side, angle = 90) => {
    try {
    const response = await apiClient.post(`/items/${id}/rotate`, { side, angle });
        return response.data;
    } catch (error) {
         throw error.response ? error.response.data : error;
    }
};

export const autoCropItemImages = async (id, side = 'both') => {
  try {
    const response = await apiClient.post(`/items/${id}/auto-crop`, { side });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Get worker queue status
export const getWorkerStatus = async () => {
  const doFetch = async () => {
    try {
      const response = await apiClient.get('/scans/queue/status', { timeout: 5000 });
      return response.data;
    } catch (error) {
      // Log non-verbose to avoid console noise
      console.debug('Worker status fetch failed:', error.message || error);
      return null;
    }
  };

  // Try once, and retry a single time after short delay on failure
  let data = await doFetch();
  if (!data) {
    await new Promise(r => setTimeout(r, 1000));
    data = await doFetch();
  }

  return data;
};
